// KaTeX macro map shared by every place we hand mathlive-authored LaTeX to KaTeX
// (the tiptap inline-math extension, its node view, and the Cloze MathTextRenderer).
//
// mathlive emits its own macros that KaTeX does not define; without these mappings KaTeX
// renders them as red error text. The right-hand sides mirror mathlive's own definitions, so
// rendering matches the editor:
//  - `\placeholder`   → empty box (KaTeX has no `\placeholder`).
//  - `\exponentialE`  → upright e, `\imaginaryI`/`\imaginaryJ` → upright i/j, `\differentialD`
//    → upright d (ISO 80000-2; emitted by the e/i/j keys and inside integrals/derivatives).
//  - `\mleft`/`\mright` → `\left`/`\right` (mathlive auto-sizes delimiters as `\mleft( … \mright)`).
//
// Keep this as the single source — adding a mapping in only one call site would fix the editor
// but still break the Cloze dropdown (or vice versa).
export const KATEX_MACROS = {
  '\\placeholder': '\\square',
  '\\exponentialE': '\\mathrm{e}',
  '\\imaginaryI': '\\mathrm{i}',
  '\\imaginaryJ': '\\mathrm{j}',
  '\\differentialD': '\\mathrm{d}',
  '\\mleft': '\\left',
  '\\mright': '\\right'
};

// Some mathlive serializations are not just missing-macro issues (which `KATEX_MACROS` covers) but
// STRUCTURAL forms KaTeX's parser rejects outright. The macro map can't fix those, so normalize the
// LaTeX before handing it to KaTeX. Display-only: the stored LaTeX stays exactly as mathlive emitted
// it (mathlive round-trips it fine), so grading/comparison are untouched.
//
// Fixed-size delimiters — the `\big`/`\Big`/`\bigg`/`\Bigg` family, optionally suffixed `l`/`m`/`r`
// — round-trip through `getValue('latex')` with the delimiter wrapped in a brace GROUP. A derivative
// evaluated at a point, for example, comes back as `…\bigm{|}_{x=3}`. KaTeX requires a BARE delimiter
// (`\bigm|`) and throws "Invalid delimiter type 'ordgroup'" on the group, so the whole formula falls
// back to red error text (bug 529207). Strip the braces to expose the delimiter KaTeX expects.
//
// The delimiter body is matched as a run of either an escaped brace (`\{`, `\}` — themselves valid
// brace delimiters) or any non-brace char, so `\bigl{\{}` normalizes to `\bigl\{` and a multi-char
// delimiter such as `\bigm{\langle}` to `\bigm\langle`.
export const toKatexLatex = (latex: string): string =>
  latex.replace(/\\(bigg?|Bigg?)([lmr]?)\{((?:\\[{}]|[^{}])+)\}/g, '\\$1$2$3');
